import type { Ref } from 'vue'

import type { Options } from '../client/sdk.gen'
import type { V1ErrorObject } from '../types/error'
import type { Result } from '../utils/monad'

import { ref } from 'vue'
import { toast } from 'vue-sonner'

import { createClient } from '../client/client/client.gen'
import i18n from '../locales'
import { useRegionStore } from '../stores/region'
import { useUserStore } from '../stores/user'
import { ApiError } from '../types/error'
import { errorToMessage } from '../utils/error'
import { fetchWithCredentials } from '../utils/fetch'
import { Err, Ok } from '../utils/monad'
import { sanitizeAnalyticsPath } from '../utils/posthog-privacy'
import { PostHogTracker } from '../utils/posthog-tracker'

interface ApiRequestInfo { method: string, url: string }

// Create a function to get the API client with current region.
// `credentials: 'include'` is required for the auth cookie to ride along on
// cross-site requests from app.spatius.ai to the API host. `onRequest` sees the
// request even when fetch itself fails, which leaves no response to read.
function getApiClient(onRequest: (request: ApiRequestInfo) => void) {
  const regionStore = useRegionStore()
  return createClient({
    baseUrl: regionStore.currentRegion.apiBaseUrl,
    fetch: (input, init) => {
      // Read method/url without constructing a Request, which would lock a stream body.
      const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url
      onRequest({ method: init?.method ?? (input instanceof Request ? input.method : 'GET'), url })
      return fetchWithCredentials(input, init)
    },
  })
}

// Every failed request is reported from here. 401 is expected (anonymous
// probes, expired sessions) and aborts are deliberate; neither is reported.
// Each distinct failure is reported once per page load, at most
// API_FAILURE_PAGE_CAP of them.
const API_FAILURE_PAGE_CAP = 30
const reportedApiFailures = new Set<string>()

function isIdSegment(segment: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment)
    || /^\d+$/.test(segment)
    || (segment.length >= 16 && /\d/.test(segment))
}

/** Request path for grouping: ids redacted, the local /proxy-api/<env> prefix dropped. */
export function apiFailurePath(url: string | undefined): string | undefined {
  if (!url)
    return undefined
  try {
    const origin = typeof window === 'undefined' ? 'http://localhost' : window.location.origin
    const path = sanitizeAnalyticsPath(new URL(url, origin).pathname.replace(/^\/proxy-api\/[^/]+/, ''))
    // Resource ids (UUIDs, numbers, long ids with digits) become :id so one
    // endpoint reads as one path.
    return path.split('/').map(segment => isIdSegment(segment) ? ':id' : segment).join('/')
  }
  catch {
    return undefined
  }
}

function reportApiFailure(request: ApiRequestInfo | undefined, error: unknown, notified: boolean): void {
  // Reporting runs inside doRequest's failure paths; it must never change their outcome.
  try {
    reportApiFailureUnsafe(request, error, notified)
  }
  catch {}
}

function reportApiFailureUnsafe(request: ApiRequestInfo | undefined, error: unknown, notified: boolean): void {
  const apiError = error instanceof ApiError ? error.error : undefined
  if (apiError?.status === 401 || (error instanceof Error && error.name === 'AbortError'))
    return
  const failureKind = apiError ? 'http' : error instanceof TypeError ? 'network' : 'client'
  const status = apiError?.status ?? null
  const code = typeof apiError?.code === 'string' ? apiError.code : undefined
  const path = apiFailurePath(request?.url)
  const key = [request?.method, path, status, code, failureKind].join(' ')
  if (reportedApiFailures.has(key) || reportedApiFailures.size >= API_FAILURE_PAGE_CAP)
    return
  reportedApiFailures.add(key)
  // Only stable identifiers leave the page: backend detail text can carry user
  // data (emails, OAuth codes), so it is never reported.
  PostHogTracker.error.apiRequestFailed({
    method: request?.method,
    path,
    status,
    error_code: code,
    error_name: apiError ? undefined : error instanceof Error ? error.name : typeof error,
    failure_kind: failureKind,
    notified,
  })
}

export interface ApiResponse<T> {
  data: T | null
  isLoading: Ref<boolean>
}

export interface PaginationOptions {
  pageSize?: number
  pageToken?: string
}

type DeepRequired<T> = {
  [P in keyof T]-?: T[P] extends object ? DeepRequired<T[P]> : T[P];
}

interface ApiResultShape<T> {
  data?: T
  error?: unknown
  response: Response
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function normalizeStatus(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isInteger(value))
    return value
  if (typeof value === 'string' && /^\d{3}$/.test(value))
    return Number(value)
  return undefined
}

function firstEnvelopeError(value: unknown): V1ErrorObject | undefined {
  if (!isRecord(value))
    return undefined

  if (Array.isArray(value.errors)) {
    const firstError = value.errors.find(isRecord)
    if (firstError)
      return firstError
    return undefined
  }

  return undefined
}

function errorPayload(value: unknown): V1ErrorObject | undefined {
  const envelopedError = firstEnvelopeError(value)
  if (envelopedError)
    return envelopedError
  if (isRecord(value) && ('status' in value || 'detail' in value))
    return value
  return undefined
}

/**
 * The API currently exposes both protobuf-style HTTP 200 error envelopes and
 * regular non-2xx responses. Normalize both before applying shared handling.
 */
function getApiError<T>({ data, error, response }: ApiResultShape<T>): ApiError | undefined {
  // Successful payloads may legitimately contain fields named `status` or
  // `detail`; only an explicit `errors` envelope makes `data` an error body.
  const structuredError = firstEnvelopeError(data) ?? errorPayload(error)
  if (!structuredError && response.ok && error == null)
    return undefined

  const responseStatus = response.ok ? undefined : response.status
  const status = normalizeStatus(structuredError?.status) ?? responseStatus
  const detail = typeof structuredError?.detail === 'string'
    ? structuredError.detail
    : typeof error === 'string'
      ? error
      : response.statusText || (status ? `Request failed with status ${status}` : 'Unknown error')

  return new ApiError({
    ...structuredError,
    ...(status === undefined ? {} : { status }),
    detail,
  })
}

export async function doRequest<
  TApiResponse,
  TOptions extends Options,
  TData = DeepRequired<NonNullable<TApiResponse extends { data: infer U } ? U : never>>,
>(
  apiFn: (params: TOptions) => Promise<TApiResponse>,
  params?: TOptions,
  options: {
    pagination?: PaginationOptions
    silent?: boolean
    handleUnauthorized?: boolean
  } = {},
): Promise<Result<ApiResponse<TData>>> {
  const isLoading = ref(true)
  let request: ApiRequestInfo | undefined
  const client = getApiClient((info) => {
    request = info
  })
  const userStore = useUserStore()
  const requestAuthGeneration = userStore.getAuthGeneration()

  // Auth is handled via an HttpOnly cookie (Set-Cookie on Login). The browser
  // attaches it automatically thanks to `credentials: 'include'` on the fetch
  // client, so no explicit Authorization header is needed here.

  try {
    // 处理分页参数 - 只在明确指定了 pagination 选项时才添加
    let processedOptions: TOptions = {
      ...params,
    } as TOptions

    // 只有当明确提供了 pagination 选项时，才添加分页参数
    if (options?.pagination) {
      const paginationParams: Record<string, string | number> = {}

      if (options.pagination.pageSize !== undefined) {
        paginationParams['pagination.pageSize'] = options.pagination.pageSize
      }

      if (options.pagination.pageToken) {
        paginationParams['pagination.pageToken'] = options.pagination.pageToken
      }

      // 合并查询参数和分页参数
      processedOptions = {
        ...processedOptions,
        query: {
          ...paginationParams,
          ...(params?.query as object),
        },
      } as TOptions
    }
    else if (params?.query) {
      // 如果没有分页选项但已有查询参数，保留原有查询参数
      processedOptions = {
        ...processedOptions,
        query: params.query,
      } as TOptions
    }

    // 处理请求体，移除空字符串
    if (processedOptions.body) {
      const body = Object.fromEntries(
        Object.entries(processedOptions.body).filter(([_, value]) => value !== ''),
      )
      processedOptions = { ...processedOptions, body } as TOptions
    }

    const apiResponse = await apiFn({ ...processedOptions, client } as TOptions)

    const { data, error, response: rawResponse } = apiResponse as ApiResultShape<TData>
    const apiError = getApiError({ data, error, response: rawResponse })

    if (apiError) {
      if (apiError.error.status === 401) {
        // A first anonymous /me probe also returns 401. Only an already
        // authenticated store represents an expired client session; resetting
        // PostHog for an anonymous probe would sever the cross-site identity.
        if (options.handleUnauthorized !== false)
          userStore.handleUnauthorized(requestAuthGeneration)
        return Err({ data: apiError, isLoading })
      }

      if (apiError.error.status === 403) {
        if (!options.silent)
          toast.error(i18n.global.t('error.forbidden'))
        reportApiFailure(request, apiError, !options.silent)
        return Err({ data: apiError, isLoading })
      }

      throw apiError
    }

    // Allow null/undefined data for responses that don't return any data (e.g., empty RegisterResponse)
    // Empty protobuf messages may be serialized as null or empty object, which is valid
    // Only throw error if response is not ok and data is null
    if (data == null && !rawResponse.ok) {
      throw new Error('Data is null')
    }

    // Return null if data is null/undefined (for empty responses)
    return Ok({ data: data ?? null, isLoading })
  }
  catch (error) {
    if (!options?.silent) {
      toast.error(errorToMessage(error) || i18n.global.t('error.unknown'))
    }
    reportApiFailure(request, error, !options?.silent)
    return Err({ data: error, isLoading })
  }
  finally {
    isLoading.value = false
  }
}

export const useApi = doRequest
